package com.kaihuoy.home.screens

import androidx.compose.runtime.*
import kotlinx.browser.window
import kotlinx.coroutines.MainScope
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import com.kaihuoy.shared.data.LoadRecord
import com.kaihuoy.web.FirebaseService
import com.kaihuoy.components.common.*
import kotlin.js.Date

private data class LoadEntry(val driverUid: String, val nodeKey: String, val load: LoadRecord)

@Composable
fun KuormatAdminTab() {
    val isMobile = window.innerWidth <= 900
    var allEntries by remember { mutableStateOf<List<LoadEntry>>(emptyList()) }
    var isLoading by remember { mutableStateOf(true) }
    var filterDriver by remember { mutableStateOf("") }
    var filterJobSite by remember { mutableStateOf("") }

    LaunchedEffect(Unit) {
        try {
            FirebaseService.database.reference("loads").valueEvents.collect { snapshot ->
                val list = mutableListOf<LoadEntry>()
                snapshot.children.forEach { driverNode ->
                    val driverUid = driverNode.key ?: return@forEach
                    driverNode.children.forEach { loadNode ->
                        try {
                            list.add(LoadEntry(driverUid, loadNode.key ?: "", loadNode.value<LoadRecord>()))
                        } catch (e: Exception) {
                            console.error("Virhe kuorman luvussa: ${loadNode.key}", e)
                        }
                    }
                }
                allEntries = list.sortedByDescending { it.load.timestamp }
                isLoading = false
            }
        } catch (e: kotlinx.coroutines.CancellationException) {
            throw e
        } catch (e: Exception) {
            console.error("Kuormien haku epäonnistui", e)
            isLoading = false
        }
    }

    val drivers = remember(allEntries) {
        allEntries.map { it.load.driverName }.filter { it.isNotBlank() }.distinct().sorted()
    }
    val jobSites = remember(allEntries, filterDriver) {
        val base = if (filterDriver.isBlank()) allEntries else allEntries.filter { it.load.driverName == filterDriver }
        base.map { it.load.jobSite }.filter { it.isNotBlank() }.distinct().sorted()
    }

    LaunchedEffect(jobSites) {
        if (filterJobSite.isNotBlank() && filterJobSite !in jobSites) filterJobSite = ""
    }

    val filtered = remember(allEntries, filterDriver, filterJobSite) {
        allEntries.filter { e ->
            (filterDriver.isBlank() || e.load.driverName == filterDriver) &&
            (filterJobSite.isBlank() || e.load.jobSite == filterJobSite)
        }
    }

    if (isLoading) {
        P { Text("Ladataan kuormia...") }
        return
    }

    Div(attrs = {
        style {
            display(DisplayStyle.Flex)
            flexWrap(FlexWrap.Wrap)
            gap(12.px)
            marginBottom(24.px)
            alignItems(AlignItems.Center)
        }
    }) {
        StyledDropdown(
            value = filterDriver,
            options = listOf(DropdownOption("", "Kaikki kuljettajat")) + drivers.toDropdownOptions(),
            placeholder = "Kaikki kuljettajat",
            label = "Kuljettaja",
            onSelect = { filterDriver = it },
            fullWidth = false,
            minWidth = 200.px
        )
        StyledDropdown(
            value = filterJobSite,
            options = listOf(DropdownOption("", "Kaikki työmaat")) + jobSites.toDropdownOptions(),
            placeholder = "Kaikki työmaat",
            label = "Työmaa",
            onSelect = { filterJobSite = it },
            fullWidth = false,
            minWidth = 200.px
        )
        Span(attrs = {
            style {
                color(KaihuTheme.TextSecondary)
                fontSize(0.9.em)
                marginLeft(8.px)
            }
        }) {
            Text("${filtered.size} kuormaa")
        }
    }

    if (filtered.isEmpty()) {
        P(attrs = { style { color(KaihuTheme.TextSecondary) } }) { Text("Ei kuormia valituilla suodattimilla.") }
        return
    }

    val byDay = remember(filtered) {
        filtered.groupBy { e ->
            if (e.load.timestamp > 0) {
                val d = Date(e.load.timestamp.toDouble())
                "${d.getFullYear()}-${d.getMonth()}-${d.getDate()}"
            } else "unknown"
        }.entries.sortedByDescending { it.key }
    }

    val expandedDays = remember { mutableStateMapOf<String, Boolean>() }

    byDay.forEach { (dayKey, dayEntries) ->
        val dayLoads = dayEntries.map { it.load }
        val dayLabel = if (dayEntries.first().load.timestamp > 0) {
            Date(dayEntries.first().load.timestamp.toDouble()).toLocaleDateString("fi-FI")
        } else "Tuntematon päivä"

        val isExpanded = expandedDays[dayKey] ?: false

        Div(attrs = {
            style {
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                gap(10.px)
                margin(8.px, 0.px, 4.px, 0.px)
            }
        }) {
            // Klikattava osa (collapse/expand)
            Div(attrs = {
                onClick { expandedDays[dayKey] = !isExpanded }
                style {
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    gap(8.px)
                    cursor("pointer")
                    flex(1)
                }
            }) {
                Span(attrs = {
                    style { fontSize(0.82.em); color(KaihuTheme.TextSecondary) }
                }) { Text(if (isExpanded) "▾" else "▸") }
                Span(attrs = {
                    style {
                        fontWeight("700")
                        fontSize(0.95.em)
                        color(KaihuTheme.TextPrimary)
                        property("white-space", "nowrap")
                    }
                }) { Text(dayLabel) }
                Span(attrs = {
                    style {
                        fontSize(0.78.em)
                        color(KaihuTheme.TextSecondary)
                        property("white-space", "nowrap")
                    }
                }) { Text("(${dayEntries.size})") }
                Div(attrs = {
                    style {
                        height(1.px)
                        flex(1)
                        backgroundColor(KaihuTheme.Divider)
                    }
                })
            }
            // Lataa PDF
            Button(attrs = {
                onClick { printDayLoadsPdf(dayLabel, dayLoads) }
                style {
                    padding(4.px, 10.px)
                    borderRadius(7.px)
                    border(1.px, LineStyle.Solid, KaihuTheme.PrimaryTeal)
                    backgroundColor(Color("transparent"))
                    color(KaihuTheme.PrimaryTeal)
                    fontSize(0.75.em)
                    fontWeight("600")
                    cursor("pointer")
                    property("white-space", "nowrap")
                }
            }) { Text("⬇ PDF") }
            // Poista päivä
            Button(attrs = {
                onClick {
                    AppFeedback.confirm(
                        title = "Poista päivän kuormat",
                        message = "Poistetaanko kaikki ${dayEntries.size} kuormaa päivältä $dayLabel?",
                        confirmText = "Poista",
                        confirmColor = KaihuTheme.Danger
                    ) {
                        MainScope().launch {
                            dayEntries.forEach { e ->
                                if (e.nodeKey.isNotBlank()) {
                                    FirebaseService.database.reference("loads")
                                        .child(e.driverUid).child(e.nodeKey).removeValue()
                                }
                            }
                        }
                    }
                }
                style {
                    padding(4.px, 10.px)
                    borderRadius(7.px)
                    border(1.px, LineStyle.Solid, KaihuTheme.Danger)
                    backgroundColor(Color("transparent"))
                    color(KaihuTheme.Danger)
                    fontSize(0.75.em)
                    fontWeight("600")
                    cursor("pointer")
                    property("white-space", "nowrap")
                }
            }) { Text("Poista") }
        }

        if (isExpanded) {
            if (isMobile) {
                dayLoads.forEach { load -> LoadCardWeb(load) }
            } else {
                LoadTable(dayLoads)
            }
        }
    }
}


@Composable
private fun LoadTable(loads: List<LoadRecord>) {
    Div(attrs = {
        style {
            overflowX("auto")
            borderRadius(16.px)
            border(1.px, LineStyle.Solid, KaihuTheme.Divider)
            backgroundColor(KaihuTheme.Surface)
            marginBottom(8.px)
        }
    }) {
        Table(attrs = {
            style {
                width(100.percent)
                property("border-collapse", "collapse")
                fontSize(0.92.em)
            }
        }) {
            Thead {
                Tr(attrs = {
                    style {
                        backgroundColor(KaihuTheme.Divider)
                        textAlign("left")
                    }
                }) {
                    listOf("Aika", "Kuljettaja", "Materiaali", "Reitti", "Työmaa").forEach { col ->
                        Th(attrs = {
                            style {
                                padding(10.px, 16.px)
                                fontWeight("600")
                                color(KaihuTheme.TextPrimary)
                                property("white-space", "nowrap")
                            }
                        }) { Text(col) }
                    }
                }
            }
            Tbody {
                loads.forEach { load ->
                    Tr(attrs = {
                        style {
                            property("border-top", "1px solid ${KaihuTheme.Divider}")
                        }
                    }) {
                        Td(attrs = { style { padding(8.px, 16.px); property("white-space", "nowrap"); color(KaihuTheme.TextSecondary) } }) {
                            Text(if (load.timestamp > 0) formatLoadDate(load.timestamp) else "–")
                        }
                        Td(attrs = { style { padding(8.px, 16.px); fontWeight("500") } }) {
                            Text(load.driverName.ifBlank { "–" })
                        }
                        Td(attrs = { style { padding(8.px, 16.px) } }) {
                            Text(load.materialType.ifBlank { "–" })
                        }
                        Td(attrs = { style { padding(8.px, 16.px); property("white-space", "nowrap") } }) {
                            Text(
                                if (load.loadingPlace.isNotBlank() || load.unloadingPlace.isNotBlank())
                                    "${load.loadingPlace} → ${load.unloadingPlace}"
                                else "–"
                            )
                        }
                        Td(attrs = { style { padding(8.px, 16.px) } }) {
                            Text(load.jobSite.ifBlank { "–" })
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun LoadCardWeb(load: LoadRecord) {
    Div(attrs = {
        style {
            backgroundColor(KaihuTheme.Surface)
            borderRadius(16.px)
            padding(16.px)
            marginBottom(12.px)
            border(1.px, LineStyle.Solid, KaihuTheme.Divider)
        }
    }) {
        B { Text(load.materialType.ifBlank { "–" }) }
        P(attrs = { style { margin(2.px, 0.px); color(KaihuTheme.TextSecondary); fontSize(0.9.em) } }) {
            Text("${load.loadingPlace} → ${load.unloadingPlace}")
        }
        P(attrs = { style { margin(2.px, 0.px); fontSize(0.9.em) } }) {
            Text("Kuljettaja: ${load.driverName.ifBlank { "–" }}")
        }
        if (load.jobSite.isNotBlank()) {
            P(attrs = { style { margin(2.px, 0.px); fontSize(0.9.em) } }) {
                Text("Työmaa: ${load.jobSite}")
            }
        }
        if (load.timestamp > 0) {
            P(attrs = { style { margin(4.px, 0.px, 0.px, 0.px); color(KaihuTheme.TextLight); fontSize(0.82.em) } }) {
                Text(formatLoadDate(load.timestamp))
            }
        }
    }
}

private fun formatLoadDate(timestamp: Long): String =
    Date(timestamp.toDouble()).toLocaleString("fi-FI")

private fun printDayLoadsPdf(dayLabel: String, loads: List<LoadRecord>) {
    val rows = loads.joinToString("") { load ->
        val aika = if (load.timestamp > 0) formatLoadDate(load.timestamp) else "–"
        val reitti = if (load.loadingPlace.isNotBlank() || load.unloadingPlace.isNotBlank())
            "${load.loadingPlace} → ${load.unloadingPlace}" else "–"
        """<tr>
            <td>$aika</td>
            <td>${load.driverName.ifBlank { "–" }}</td>
            <td>${load.materialType.ifBlank { "–" }}</td>
            <td>$reitti</td>
            <td>${load.jobSite.ifBlank { "–" }}</td>
        </tr>"""
    }
    val html = """<!DOCTYPE html><html><head><meta charset="UTF-8">
        <title>Kuormat $dayLabel</title>
        <style>
            body { font-family: Arial, sans-serif; font-size: 12px; margin: 24px; }
            h2 { margin-bottom: 12px; }
            table { width: 100%; border-collapse: collapse; }
            th, td { border: 1px solid #ddd; padding: 6px 10px; text-align: left; }
            th { background: #f0f0f0; font-weight: 600; }
            tr:nth-child(even) { background: #fafafa; }
        </style>
    </head><body>
        <h2>Kuormat – $dayLabel</h2>
        <table>
            <thead><tr>
                <th>Aika</th><th>Kuljettaja</th><th>Materiaali</th><th>Reitti</th><th>Työmaa</th>
            </tr></thead>
            <tbody>$rows</tbody>
        </table>
    </body></html>"""

    val w = window.open("", "_blank")
    w?.document?.write(html)
    w?.document?.close()
    w?.print()
}
